import { IssueCreationSource, IntegrationProvider } from '@gripp/shared-logic';
import { useTranslation } from 'react-i18next';
import { Tag } from '../crop-circle/Components/Tag/Tag';

/**
 * Map from `IssueCreationSource` to the human-readable provider display name
 * shown in the attribution tag.  Scanner and AnonymousWeb have no integration
 * provider and return `undefined` so callers can decide whether to render.
 *
 * String literal keys are used intentionally so the lookup stays correct when
 * the enum import is replaced by a test mock (the enum value IS the string).
 */
// IssueCreationSource values that map to a provider display name.
// Cast type ensures TS catches typos but doesn't use computed enum keys.
export const INTEGRATION_PROVIDER_DISPLAY_NAMES: Partial<
  Record<string, string>
> = {
  GEOTAB: 'GeoTab',
  JOHN_DEERE: 'John Deere Operations',
} as Partial<Record<IssueCreationSource, string>>;

/**
 * The set of `IssueCreationSource` string values that represent integration
 * providers (i.e. sources that should show an attribution tag).
 *
 * Stored as a `Set<string>` rather than `Set<IssueCreationSource>` so membership
 * tests remain correct when the module is mocked in tests.
 */
export const INTEGRATION_CREATION_SOURCES: ReadonlySet<string> = new Set<string>(
  ['GEOTAB', 'JOHN_DEERE']
);

/**
 * Map from `IntegrationProvider` enum values to human-readable display names,
 * for use in digest card attribution pills.
 *
 * String literal keys are used intentionally to remain correct even when
 * the enum import is replaced by a test mock (the enum value is the string
 * value, so the lookup still works with string inputs).
 */
export const INTEGRATION_PROVIDER_NAMES: Record<string, string> = {
  GEOTAB: 'GeoTab',
  JOHN_DEERE: 'John Deere Operations',
  CASE_IH: 'Case IH',
} as Record<IntegrationProvider, string>;

export interface IntegrationAttributionTagProps {
  /**
   * The creation source to display attribution for.  When the source is not
   * an integration source (e.g. SCANNER, ANONYMOUSWEB) this component renders
   * nothing.
   */
  createdFrom: IssueCreationSource;
  /** Test ID for testing */
  testID?: string;
}

/**
 * Renders a `<Tag status="join" text="Auto-synced" />` badge for
 * integration-sourced data, productionising the `mockup/jd-ops-routine-attribution`
 * pattern.
 *
 * - GEOTAB and JOHN_DEERE sources → shows tag
 * - SCANNER and ANONYMOUSWEB sources → renders nothing
 * - No provider logo for MVP
 */
export const IntegrationAttributionTag = ({
  createdFrom,
  testID,
}: IntegrationAttributionTagProps) => {
  const { t } = useTranslation();

  if (!INTEGRATION_CREATION_SOURCES.has(createdFrom)) {
    return null;
  }

  return (
    <Tag
      status="join"
      text={t('integrations.attribution.autoSynced')}
      testID={testID}
    />
  );
};

IntegrationAttributionTag.displayName = 'IntegrationAttributionTag';
